import React, { useMemo, useState } from 'react';
import { formatDate, formatTime, formatCurrency } from '../../utils/helpers';
import PriceCalculationService from '../../services/priceCalculator';

const BookingSummary = ({
  searchData,
  selectedVehicle,
  extrasData = null,
  step = 0,
  routeInfo,
  onContinue,
  isProcessing = false
}) => {
  const [showMoreIncluded, setShowMoreIncluded] = useState(false);
  const [couponCode, setCouponCode] = useState('');

  // --- helpers ---------------------------------------------------------------
  const isHourlyType = useMemo(() => {
    const t = String(searchData?.type || '').toLowerCase();
    return t.includes('hourly') || searchData?.subService === 'hourly';
  }, [searchData?.type, searchData?.subService]);

  const currency = selectedVehicle?.pricing?.currency || searchData?.currency || 'USD';

  const pickupAtDate = searchData?.pickupDate && searchData?.pickupTime
    ? `${searchData.pickupDate}T${searchData.pickupTime}`
    : new Date().toISOString();

  const isTransferType = useMemo(() => {
    const t = String(searchData?.type || '').toLowerCase();
    return t.includes('transfer') || searchData?.subService === 'transfer';
  }, [searchData?.type, searchData?.subService]);

  const returnMultiplier = isTransferType && searchData?.showReturn ? 2 : 1;
  const waitingMins = Number(searchData?.waitingMins) || 0;
  const toNum = (v) =>
    typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[^\d.]/g, '')) || 0;

  const distanceKm = (routeInfo?.distanceKm ?? toNum(routeInfo?.distance))
    || (routeInfo?.distanceValue ? routeInfo.distanceValue / 1000 : 0)
    || toNum(searchData?.distance) || 0;

  const priceResult = isHourlyType
    ? PriceCalculationService.calculateHourlyPrice(selectedVehicle, Number(searchData?.hours || 0), distanceKm, pickupAtDate)
    : PriceCalculationService.calculateTransferPrice(selectedVehicle, distanceKm, pickupAtDate, extrasData, returnMultiplier, waitingMins);

  const finalAmount = priceResult?.total || 0;

  return (
    <aside className="sidebar-container glass-panel rounded-[2rem] border-white/10 bg-[#0d1320]/70 flex flex-col h-full shadow-2xl overflow-hidden backdrop-blur-3xl">
      <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
        <h2 className="text-lg font-black text-white uppercase tracking-tight mb-6">Your Summary</h2>

        {/* Journey Indicator */}
        <div className="mb-6 space-y-0.5 relative">
          <p className="font-mono text-[9px] text-gray-500 uppercase tracking-[0.2em] mb-3">Outward Journey</p>
          
          <div className="flex gap-4 items-start">
            <div className="w-1 h-8 rounded-full bg-blue-500 flex-shrink-0 mt-1 shadow-[0_0_10px_rgba(59,130,246,0.3)]"></div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-white leading-tight uppercase italic break-words">{searchData?.fromPlace || 'Select Pickup'}</p>
              <p className="text-[10px] text-gray-500 font-medium uppercase tracking-widest mt-0.5">Pickup Location</p>
            </div>
          </div>

          <div className="w-1 h-3 ml-0 border-l border-dashed border-white/20 my-1"></div>

          <div className="flex gap-4 items-start">
            <div className="w-1 h-8 rounded-full bg-yellow-500 flex-shrink-0 mt-1 shadow-[0_0_10px_rgba(234,179,8,0.3)]"></div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-white leading-tight uppercase italic break-words">{searchData?.toPlace || (isHourlyType ? 'Hourly Chauffeur' : 'Select Destination')}</p>
              <p className="text-[10px] text-gray-500 font-medium uppercase tracking-widest mt-0.5">Destination</p>
            </div>
          </div>
        </div>

        {/* Trip Details Row */}
        <div className="border-t border-white/5 py-4">
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold text-gray-400 uppercase tracking-widest">
            <span>{formatDate(searchData?.pickupDate)}</span>
            <span className="opacity-20">•</span>
            <span>{formatTime(searchData?.pickupTime)}</span>
            <span className="opacity-20">•</span>
            <span className="flex items-center gap-1.5">
              <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
              </svg>
              {searchData?.passengers || 1} Pax
            </span>
          </div>
        </div>

        {/* Selected Vehicle Card */}
        {selectedVehicle && (
          <div className="border-t border-white/5 py-4">
            <div className="flex items-center gap-4 bg-white/5 border border-white/5 rounded-2xl p-3">
              <div className="w-12 h-10 rounded-lg overflow-hidden bg-white/5 border border-white/10 flex-shrink-0">
                <img 
                  src={PriceCalculationService.getVehicleImage(selectedVehicle)} 
                  alt="" 
                  className="w-full h-full object-cover" 
                  onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<span class="text-lg">🚗</span>'; }}
                />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-black text-white uppercase italic truncate tracking-tight">{selectedVehicle.className}</p>
                <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest mt-0.5">{selectedVehicle.paxMax} Pax • {selectedVehicle.bagMax} bags</p>
              </div>
            </div>
          </div>
        )}

        {/* Pricing Breakdown */}
        <div className="border-t border-white/5 py-4 space-y-3">
          <div className="flex justify-between items-center text-[11px]">
            <span className="text-gray-500 font-bold uppercase tracking-widest">Base Rate</span>
            <span className="text-white font-mono font-bold leading-none">{formatCurrency((isHourlyType ? priceResult?.basePrice : priceResult?.baseFare) || 0, currency)}</span>
          </div>
          
          {priceResult?.extrasTotal > 0 && (
            <div className="flex justify-between items-center text-[11px]">
              <span className="text-blue-400 font-black uppercase tracking-widest">Add-ons & Extras</span>
              <span className="text-blue-400 font-mono font-bold leading-none">+{formatCurrency(priceResult.extrasTotal, currency)}</span>
            </div>
          )}

          <div className="flex justify-between items-end border-t border-white/10 pt-4 mt-2">
            <div>
              <p className="text-[10px] text-blue-500 font-black uppercase tracking-[0.2em] mb-1">Total Due</p>
              <p className="text-[9px] text-gray-600 font-bold uppercase tracking-widest leading-none">All taxes included</p>
            </div>
            <p className="text-3xl font-black text-white tracking-tighter leading-none font-mono">
              {formatCurrency(finalAmount, currency)}
            </p>
          </div>
        </div>

        {/* Promo Code Toggle */}
        <div className="border-t border-white/5 py-4">
          <button 
            type="button" 
            className="text-[10px] text-blue-500 font-black uppercase tracking-widest hover:text-white transition-colors flex items-center gap-1.5"
            onClick={() => setCouponCode(couponCode === null ? '' : null)}
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            Have a promo code?
          </button>
          
          {couponCode !== null && (
            <div className="mt-3 flex gap-2">
              <input 
                type="text" 
                placeholder="PROMO10" 
                className="flex-1 h-9 bg-white/5 border border-white/10 rounded-lg px-3 text-[10px] font-black text-white uppercase tracking-widest focus:border-blue-500 outline-none placeholder:text-gray-700" 
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
              />
              <button type="button" className="h-9 px-4 bg-white/10 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-white/20 transition-all">Apply</button>
            </div>
          )}
        </div>

        {/* Guarantees */}
        <div className="border-t border-white/5 py-4">
          <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-3">What's included</p>
          <ul className="space-y-2">
            {[
              'Private transfer', 'Flight tracking', 'Free cancellation 24h', 
              'Meet & Greet', 'Free wait time', 'Licensed fleet'
            ].slice(0, showMoreIncluded ? 6 : 3).map(item => (
              <li key={item} className="flex items-center gap-2 text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                {item}
              </li>
            ))}
          </ul>
          <button 
            type="button" 
            className="text-[10px] text-blue-500 font-black uppercase tracking-widest mt-3 flex items-center gap-1 hover:text-white transition-colors"
            onClick={() => setShowMoreIncluded(!showMoreIncluded)}
          >
            {showMoreIncluded ? 'Show less' : `+${3} more`}
          </button>
        </div>

        {/* Payment Icons */}
        <div className="pt-4 border-t border-white/5 flex flex-wrap gap-2 opacity-30">
          {['Visa', 'MC', 'Amex', 'Apple', 'GPay'].map(p => (
            <span key={p} className="text-[8px] font-mono border border-white/20 px-1.5 py-0.5 rounded leading-none text-white">{p}</span>
          ))}
        </div>
      </div>

      {/* Sticky CTA for Sidebar */}
      {onContinue && (
        <div className="p-6 border-t border-white/10 bg-[#0d1320]/80 backdrop-blur-md">
          <button 
            onClick={onContinue}
            disabled={!selectedVehicle || isProcessing}
            className={`
              w-full h-14 rounded-2xl flex items-center justify-center gap-2 font-black text-xs uppercase tracking-[0.2em] transition-all shadow-2xl
              ${!selectedVehicle || isProcessing 
                ? 'bg-white/5 text-gray-600 cursor-not-allowed border border-white/5' 
                : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:to-blue-600 shadow-blue-500/20 active:scale-95'
              }
            `}
          >
            {isProcessing ? 'Processing...' : (
              <>
                <span>Continue</span>
                <span className="opacity-30">·</span>
                <span className="font-mono">{formatCurrency(finalAmount, currency)}</span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </>
            )}
          </button>
        </div>
      )}
    </aside>
  );
};

export default BookingSummary;
